/* eslint-disable no-console */
import store from "../Redux/store";
import newChatSound from "../assets/newchat.ogg";
import {
  setConnectionStatus,
  addMessage,
  updateMessage,
  setTypingUser,
  incrementUnreadCount,
  markMessageFailed,
  updateMessageStatus,
  clearUnreadCount,
  updateUserOnlineStatus,
  applyPresenceSnapshot,
  updateNotificationCounts,
} from "../Redux/new-chat-store/chatSlice";
import { api_without_cancellation, buildTicketedWebSocketURL } from "../api/api";
import {
  clearWebSocketStatus,
  handleWebSocketClose,
  handleWebSocketTicketError,
} from "../auth/websocketLifecycle";

const SOCKET_PATH = "/ws/chat/";

class WebSocketManager {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.messageQueue = [];
    this.isConnecting = false;
    this.reconnectTimeout = null;
    this.pingInterval = null;
    this.pendingMessages = new Map(); // Track pending messages for status updates
    this.reconnectBlocked = false;
    this.terminalCloseCode = null;
    this.subscribers = new Map();
    this.chatNotificationAudio = null;
  }

  subscribe(type, listener) {
    if (!this.subscribers.has(type)) this.subscribers.set(type, new Set());
    this.subscribers.get(type).add(listener);
    return () => this.subscribers.get(type)?.delete(listener);
  }

  notify(type, payload) {
    this.subscribers.get(type)?.forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        console.error("Chat event subscriber failed:", error);
      }
    });
  }

  static getInstance() {
    if (!this.instance) {
      this.instance = new WebSocketManager();
    }
    return this.instance;
  }

  async connect() {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return Promise.resolve();
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.isConnecting = true;
    store.dispatch(setConnectionStatus("connecting"));

    console.log("Attempting to connect to WebSocket...");

    let wsUrl;
    try {
      wsUrl = await buildTicketedWebSocketURL(SOCKET_PATH);
    } catch (error) {
      this.isConnecting = false;
      if (handleWebSocketTicketError(error, SOCKET_PATH)) {
        this.handleConnectionError("Failed to obtain WebSocket ticket");
      } else {
        this.reconnectBlocked = true;
        store.dispatch(setConnectionStatus("error"));
      }
      throw error;
    }

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(wsUrl);

        const connectionTimeout = setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
            console.error("WebSocket connection timeout");
            this.ws.close();
            this.handleConnectionError("Connection timeout");
            reject(new Error("Connection timeout"));
          }
        }, 10000);

        this.ws.onopen = () => {
          console.log("WebSocket connected successfully");
          clearTimeout(connectionTimeout);
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.reconnectBlocked = false;
          this.terminalCloseCode = null;
          clearWebSocketStatus(SOCKET_PATH);
          store.dispatch(setConnectionStatus("connected"));
          this.notify("connected", {});

          // Start ping interval
          this.pingInterval = setInterval(() => {
            if (this.ws?.readyState === WebSocket.OPEN) {
              this.ws.send(
                JSON.stringify({
                  command: "ping",
                  timestamp: Date.now(),
                })
              );
            }
          }, 30000);

          this.processMessageQueue();
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log("WebSocket message received:", data);
            this.handleMessage(data);
          } catch (error) {
            console.error("Failed to parse WebSocket message:", error, event.data);
          }
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
          }

          console.log("WebSocket disconnected:", {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          });

          this.isConnecting = false;
          store.dispatch(setConnectionStatus("disconnected"));
          this.handleCloseEvent(event);
        };

        this.ws.onerror = (error) => {
          clearTimeout(connectionTimeout);
          console.error("WebSocket error:", error);
          this.isConnecting = false;
          this.handleConnectionError("WebSocket error occurred");
          reject(error);
        };
      } catch (error) {
        console.error("Failed to create WebSocket:", error);
        this.isConnecting = false;
        this.handleConnectionError("Failed to create WebSocket connection");
        reject(error);
      }
    });
  }

  handleCloseEvent(event) {
    const { code, reason } = event;
    if (!handleWebSocketClose(event, SOCKET_PATH)) {
      this.reconnectBlocked = true;
      this.terminalCloseCode = code;
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      store.dispatch(setConnectionStatus("error"));
      return;
    }

    if (code === 4401) {
      this.scheduleReconnect();
      return;
    }

    switch (code) {
      case 1000:
        console.log("WebSocket closed normally");
        break;
      case 1001:
        console.log("WebSocket closed: going away");
        this.scheduleReconnect();
        break;
      case 1006:
        console.log("WebSocket closed abnormally");
        this.scheduleReconnect();
        break;
      default:
        console.log(`WebSocket closed with code: ${code}, reason: ${reason}`);
        if (!event.wasClean && this.shouldReconnect(code)) {
          this.scheduleReconnect();
        }
    }
  }

  shouldReconnect(code) {
    if (code >= 4000 && code <= 4999) {
      return false;
    }
    return this.reconnectAttempts < this.maxReconnectAttempts;
  }

  handleConnectionError(message) {
    console.error("Connection error:", message);
    store.dispatch(setConnectionStatus("error"));

    if (!this.reconnectBlocked && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.scheduleReconnect();
    }
  }

  scheduleReconnect() {
    if (this.reconnectBlocked || this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      store.dispatch(setConnectionStatus("error"));
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);

    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);

    this.reconnectTimeout = setTimeout(() => {
      console.log(`Reconnecting... Attempt ${this.reconnectAttempts}`);
      this.connect().catch((error) => {
        console.error("Reconnection failed:", error);
      });
    }, delay);
  }

  processMessageQueue() {
    console.log(`Processing ${this.messageQueue.length} queued messages`);
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.send(message);
    }
  }

  send(message) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(message));
        console.log("Message sent:", message);
        return true;
      } catch (error) {
        console.error("Failed to send message:", error);
        this.messageQueue.push(message);
        return false;
      }
    } else {
      console.log("WebSocket not ready, queuing message:", message);
      this.messageQueue.push(message);
      if (this.terminalCloseCode === 4400) {
        this.reconnectBlocked = false;
        this.terminalCloseCode = null;
        this.connect().catch((error) => {
          console.error("WebSocket retry after corrected message failed:", error);
        });
      }
      return false;
    }
  }

  handleMessage(data) {
    const { type, ...payload } = data;
    this.notify(type, data);

    switch (type) {
      case "call.event":
        break;
      case "message.new":
        this.handleNewMessage(payload);
        break;
      case "message.sent":
        this.handleMessageSent(payload);
        break;
      case "message.delivered":
        this.handleMessageDelivered(payload);
        break;
      case "message.read":
        this.handleMessageRead(payload);
        break;
      case "messages.marked_read":
        this.handleMessagesMarkedRead(payload);
        break;
      case "thread.created":
        this.handleThreadCreated(payload);
        break;
      case "thread.updated":
        this.handleThreadUpdated(payload);
        break;
      case "inbox.update":
        this.handleInboxUpdate(payload);
        break;
      case "typing.indicator":
        this.handleTypingIndicator(payload);
        break;
      case "presence.update":
        this.handlePresenceUpdate(payload);
        break;
      case "presence.snapshot":
        this.handlePresenceSnapshot(payload);
        break;
      case "notification.count_updated":
        this.handleNotificationCountUpdate(payload);
        break;
      case "error":
        this.handleServerError(payload);
        break;
      case "pong":
        console.log("Received pong");
        break;
      default:
        console.warn("Unknown message type:", type, payload);
    }
  }

  handleNewMessage(payload) {
    console.log("New message received:", payload);
    const { message } = payload;
    if (!message) return;
    const state = store.getState();
    const userId = localStorage.getItem("loggedInUser") || state.chat2.currentUser?.id;
    const senderId = message.sender?.id ?? message.sender_id;

    // Check if this is our own message (shouldn't happen with proper server logic)
    const isOwn =
      Boolean(userId) &&
      senderId !== null &&
      senderId !== undefined &&
      String(senderId) === String(userId);
    if (isOwn && message.message_type !== "call") {
      console.log("Skipping own message from message.new");
      return;
    }

    const alreadyReceived = state.chat2.messages[message.thread_id]?.items.some(
      (item) =>
        (item.id && String(item.id) === String(message.id)) ||
        (item.client_message_id && item.client_message_id === message.client_message_id)
    );

    // Add message to store
    store.dispatch(addMessage({ threadId: message.thread_id, message }));

    if (!isOwn && !alreadyReceived && message.message_type !== "call") {
      this.playChatNotificationSound();
    }

    // Update unread count if not on current thread
    const currentThreadId = state.chat2.selectedUser?.threadId || state.chat2.selectedUser?.id;

    if (!isOwn && currentThreadId !== message.thread_id) {
      console.log("New message for different thread, incrementing unread count:", message);
      store.dispatch(
        incrementUnreadCount({ threadId: message.thread_id, content: message.content })
      );
    }

    // Refresh user list to show new message preview
    // this.refreshCurrentUserList();
  }

  playChatNotificationSound() {
    if (/^\/chat(\/|$)/i.test(window.location.pathname)) return;
    try {
      if (!this.chatNotificationAudio) {
        this.chatNotificationAudio = new Audio(newChatSound);
        this.chatNotificationAudio.preload = "auto";
      }
      this.chatNotificationAudio.currentTime = 0;
      this.chatNotificationAudio.play()?.catch((error) => {
        console.warn("Unable to play chat notification sound:", error);
      });
    } catch (error) {
      // Audio failure must not interrupt incoming messages or unread counts.
      console.warn("Unable to initialize chat notification sound:", error);
    }
  }

  handleMessageSent(payload) {
    console.log("Message sent confirmation:", payload);
    const { message } = payload;

    // Update the optimistic message with server data
    store.dispatch(
      updateMessage({
        threadId: message.thread_id,
        clientMessageId: message.client_message_id,
        serverMessage: {
          ...message,
          status: "sent",
        },
      })
    );

    // Remove from pending messages
    if (message.client_message_id) {
      this.pendingMessages.delete(message.client_message_id);
    }

    // Refresh user list to show sent message preview
    // this.refreshCurrentUserList();
  }

  handleMessageDelivered(payload) {
    console.log("Message delivered:", payload);
    const { message_id, thread_id } = payload;

    store.dispatch(
      updateMessageStatus({
        threadId: thread_id,
        messageId: message_id,
        status: "delivered",
      })
    );
  }

  handleMessageRead(payload) {
    console.log("Message read by other user:", payload);
    const { message_id, thread_id, message_ids, user_id, _read_at } = payload;

    const userId = localStorage.getItem("loggedInUser");

    // Only process if this is not our own read receipt
    if (user_id !== Number(userId)) {
      if (message_ids && Array.isArray(message_ids)) {
        // Multiple messages read by another user
        message_ids.forEach((msgId) => {
          store.dispatch(
            updateMessageStatus({
              threadId: thread_id,
              messageId: msgId,
              // status: "read",
            })
          );
        });
      } else if (message_id) {
        // Single message read by another user
        store.dispatch(
          updateMessageStatus({
            threadId: thread_id,
            messageId: message_id,
            // status: "read",
          })
        );
      }
    }
  }

  handleMessagesMarkedRead(payload) {
    console.log("Messages marked as read confirmation:", payload);
    const { thread_id, message_ids, total_unread } = payload;

    // This is the confirmation that our read operation was successful
    if (message_ids && Array.isArray(message_ids)) {
      // Update local message status
      message_ids.forEach((msgId) => {
        store.dispatch(
          updateMessageStatus({
            threadId: thread_id,
            messageId: msgId,
            status: "read",
          })
        );
      });
    }

    // Update unread count based on server response
    if (typeof total_unread === "number") {
      store.dispatch(clearUnreadCount({ threadId: thread_id }));
    }
  }

  //  Handle notification count updates
  handleNotificationCountUpdate(payload) {
    const { counts, timestamp, notification_type } = payload;
    if (notification_type !== "chat") return;
    console.log(counts);
    const state = store.getState();
    const isOnChatPage = /^\/chat(\/|$)/.test(window.location.pathname);
    const currentThreadId = isOnChatPage
      ? state.chat2.selectedUser?.threadId || state.chat2.selectedUser?.id
      : null;
    const selectedFirm = state.chat2.selectedFirm;

    const processedCounts = { ...counts };

    if (
      processedCounts.thread &&
      currentThreadId &&
      processedCounts.thread[currentThreadId] !== undefined
    ) {
      const currentThreadCount = processedCounts.thread[currentThreadId];

      processedCounts.thread = { ...processedCounts.thread };
      delete processedCounts.thread[currentThreadId];

      if (
        selectedFirm &&
        processedCounts.firm &&
        processedCounts.firm[selectedFirm.id] !== undefined
      ) {
        processedCounts.firm = { ...processedCounts.firm };
        processedCounts.firm[selectedFirm.id] = Math.max(
          0,
          processedCounts.firm[selectedFirm.id] - currentThreadCount
        );
      }

      if (processedCounts.global?.unread !== undefined) {
        processedCounts.global = {
          ...processedCounts.global,
          unread: Math.max(0, processedCounts.global.unread - currentThreadCount),
        };
      }
    }
    console.log(processedCounts);

    store.dispatch(
      updateNotificationCounts({
        counts: processedCounts,
        timestamp: timestamp || Date.now(),
      })
    );
  }

  // NEW: Handle thread creation events
  handleThreadCreated(payload) {
    console.log("Thread created event received:", payload);
    const { thread } = payload;
    const userId = localStorage.getItem("loggedInUser");

    // Check if current user is involved in this thread
    const isUserInvolved =
      thread.members &&
      thread.members.some(
        (member) => member.user?.id === Number(userId) || member.user?.id === Number(userId)
      );

    if (isUserInvolved) {
      console.log("User is involved in new thread, refreshing user list");

      // Refresh user list to show the new thread immediately
      this.refreshCurrentUserList();

      console.log("Thread created - user list refresh triggered for:", {
        threadId: thread.id,
        userId: Number(userId),
        isUserInvolved,
        members: thread.members?.map((m) => ({
          id: m.id || m.user?.id,
          name: m.user?.display_name,
        })),
      });
    }
  }

  // Handle thread updates
  handleThreadUpdated(payload) {
    console.log("Thread updated event received:", payload);
    const { thread } = payload;

    // Refresh user list to show updated thread info
    this.refreshCurrentUserList();

    console.log("Thread updated - user list refresh triggered for thread:", thread.id);
  }

  // Handle general inbox updates
  handleInboxUpdate(payload) {
    console.log("Inbox update event received:", payload);
    const { firm_id, user_id, action, thread_id } = payload;
    const currentUserId = localStorage.getItem("loggedInUser");

    // Get current state to check if this affects current view
    const state = store.getState();
    const selectedFirm = state.chat2.selectedFirm;

    // Check if this update affects current user/firm
    const shouldRefresh =
      selectedFirm &&
      (selectedFirm.id === firm_id || !firm_id) &&
      (!user_id || user_id === Number(currentUserId));

    if (shouldRefresh) {
      console.log("Inbox update affects current view, refreshing user list", {
        action,
        firm_id,
        user_id,
        thread_id,
        currentFirm: selectedFirm?.id,
        currentUser: currentUserId,
      });

      this.refreshCurrentUserList();
    }
  }

  handleTypingIndicator(payload) {
    const { thread_id, user_id, is_typing, user_name } = payload;
    store.dispatch(
      setTypingUser({
        threadId: thread_id,
        userId: user_id,
        isTyping: is_typing,
        userName: user_name,
      })
    );
  }

  handlePresenceUpdate(payload) {
    console.log("Presence update received:", payload);
    const { user_id, is_online } = payload;

    // Update user online status in Redux store
    store.dispatch(
      updateUserOnlineStatus({
        userId: user_id,
        isOnline: is_online,
      })
    );

    console.log(`User ${user_id} is now ${is_online ? "online" : "offline"}`);
  }

  handlePresenceSnapshot(payload) {
    store.dispatch(applyPresenceSnapshot({ onlineUserIds: payload.online_user_ids ?? [] }));
  }

  handleServerError(payload) {
    console.error("Server error:", payload);

    // Handle message send failures
    if (payload.client_message_id) {
      store.dispatch(
        markMessageFailed({
          threadId: payload.thread_id,
          clientMessageId: payload.client_message_id,
        })
      );
    }
  }

  // NEW: Helper method to refresh current user list
  refreshCurrentUserList() {
    const state = store.getState();
    const selectedFirm = state.chat2.selectedFirm;

    if (!selectedFirm) {
      console.log("No firm selected, skipping user list refresh");
      return;
    }

    console.log("Refreshing user list for current firm:", selectedFirm.name, selectedFirm.type);

    const userSearch = state.chat2.userSearch || "";

    // Use dynamic import to avoid circular dependency issues
    import("../Redux/new-chat-store/chatSlice")
      .then(({ fetchUsers, fetchProviderClient }) => {
        if (selectedFirm.type === "attorney") {
          console.log("Refreshing attorney users for firm:", selectedFirm.id);
          store.dispatch(
            fetchUsers({
              firmId: selectedFirm.id,
              search: userSearch,
            })
          );
        } else if (selectedFirm.type === "provider") {
          console.log("Refreshing provider clients for firm:", selectedFirm.id);
          store.dispatch(
            fetchProviderClient({
              firmId: selectedFirm.id,
              search: userSearch,
            })
          );
        }
      })
      .catch((error) => {
        console.error("Failed to refresh user list:", error);
      });
  }

  sendMessage(threadId, content, clientMessageId = null, moreOptions = {}) {
    const messageId = clientMessageId || crypto.randomUUID();
    console.log(moreOptions);
    const message = {
      command: "send_message",
      thread_id: threadId,
      content,
      client_message_id: messageId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(Object.keys(moreOptions).length > 0 && {
        context_ref: {
          case_id: moreOptions?.case_id,
          client_id: moreOptions?.client_id,
        },
      }),
    };
    console.log(message);

    // Track pending message
    this.pendingMessages.set(messageId, {
      threadId,
      content,
      timestamp: Date.now(),
    });

    console.log("Sending message:", message);
    const success = this.send(message);

    if (!success) {
      console.log("WebSocket send failed, using HTTP fallback");
      this.fallbackSendMessage(threadId, content, messageId);
    }

    return message;
  }

  async fallbackSendMessage(threadId, content, clientMessageId) {
    try {
      console.log("Sending message via HTTP fallback");
      const body = {
        content,
        client_message_id: clientMessageId,
      };

      const response = await api_without_cancellation.post(
        `/api/chat/threads/${threadId}/messages/`,
        body
      );

      const result = response.data;
      console.log("HTTP fallback successful:", result);

      // Update message status
      store.dispatch(
        updateMessage({
          threadId,
          clientMessageId,
          serverMessage: {
            ...result,
            status: "sent",
          },
        })
      );

      // Remove from pending
      this.pendingMessages.delete(clientMessageId);

      // Try to reconnect WebSocket
      console.log("Attempting to reconnect WebSocket after HTTP fallback");
      this.connect().catch((error) => {
        console.error("WebSocket reconnection after fallback failed:", error);
      });
    } catch (error) {
      console.error("Fallback message send failed:", error);
      store.dispatch(markMessageFailed({ threadId, clientMessageId }));

      // Remove from pending
      this.pendingMessages.delete(clientMessageId);

      // Try to reconnect
      console.log("Attempting to reconnect WebSocket after HTTP fallback failure");
      this.connect().catch((reconnectError) => {
        console.error("WebSocket reconnection after fallback failure failed:", reconnectError);
      });
    }
  }

  markTyping(threadId, isTyping) {
    this.send({
      command: "mark_typing",
      thread_id: threadId,
      is_typing: isTyping,
    });
  }

  // Mark messages as read via WebSocket
  markMessagesAsRead(threadId, messageIds) {
    console.log("Marking messages as read via WebSocket:", { threadId, messageIds });

    const command = {
      command: "mark_read",
      thread_id: threadId,
      message_ids: messageIds,
    };

    console.log("Sending mark_read command:", command);
    const success = this.send(command);

    if (!success) {
      console.log("WebSocket mark_read failed, will queue for later");
      // The message will be queued automatically by the send() method
    }

    return success;
  }

  async testConnection() {
    try {
      console.log("Testing WebSocket connection...");
      await this.connect();
      console.log("WebSocket connection test successful");
      return true;
    } catch (error) {
      console.error("WebSocket connection test failed:", error);
      return false;
    }
  }

  getConnectionInfo() {
    return {
      readyState: this.ws?.readyState,
      url: this.ws?.url,
      reconnectAttempts: this.reconnectAttempts,
      isConnecting: this.isConnecting,
      queuedMessages: this.messageQueue.length,
      pendingMessages: this.pendingMessages.size,
    };
  }

  disconnect() {
    console.log("Manually disconnecting WebSocket");

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    this.reconnectAttempts = this.maxReconnectAttempts;

    if (this.ws) {
      this.ws.close(1000, "Manual disconnect");
      this.ws = null;
    }

    this.pendingMessages.clear();
    this.chatNotificationAudio?.pause();
    this.chatNotificationAudio = null;
    store.dispatch(setConnectionStatus("disconnected"));
  }

  // Handle pending message timeout
  checkPendingMessages() {
    const now = Date.now();
    const timeout = 30000; // 30 seconds

    for (const [clientMessageId, messageData] of this.pendingMessages.entries()) {
      if (now - messageData.timestamp > timeout) {
        console.warn("Message timeout:", clientMessageId);
        store.dispatch(
          markMessageFailed({
            threadId: messageData.threadId,
            clientMessageId,
          })
        );
        this.pendingMessages.delete(clientMessageId);
      }
    }
  }
}

const wsManager = WebSocketManager.getInstance();
setInterval(() => {
  wsManager.checkPendingMessages();
}, 5000);

export default wsManager;
